import React, { useCallback, useEffect, useState } from "react"
import Button from "@mui/material/Button"
import IconButton from "@mui/material/IconButton"
import Tooltip from "@mui/material/Tooltip"
import ToggleButton from "@mui/material/ToggleButton"
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup"
import Checkbox from "@mui/material/Checkbox"
import FormControlLabel from "@mui/material/FormControlLabel"
import OpenInFullIcon from "@mui/icons-material/OpenInFull"
import SyncIcon from "@mui/icons-material/Sync"
import {
  LineChart,
  Line,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  CartesianGrid,
  XAxis,
  YAxis,
  Legend,
} from "recharts"
import { getEventInsights } from "services/stat"
import { syncZoomAttendance } from "services/event"
import { getUnixEpochTime } from "utils/time"
import { verifyResponse } from "utils/httpRequest"
import { SESSION_VARIABLES } from "constants/system"
import LoadingScreen from "components/common/LoadingScreen"
import DataTable from "components/common/DataTable/DataTable"
import {
  StyledKpiGrid,
  StyledKpiCard,
  StyledInsightGrid,
  StyledInsightPanel,
  StyledInsightList,
  StyledAgentTrendChart,
} from "./Home.style"

const TIER_FILTERS = [
  { key: "all", label: "All Tiers" },
  { key: "free", label: "Free" },
  { key: "silver", label: "Silver" },
  { key: "gold", label: "Gold" },
  { key: "platinum", label: "Platinum" },
  { key: "product", label: "Product" },
]

const EVENT_TABLE_FIELDS = [
  { key: "title", label: "Event", width: 260 },
  { key: "eventType", label: "Type", width: 120 },
  { key: "startDate", label: "Start", width: 110 },
  { key: "tierLabel", label: "Tier", width: 100 },
  { key: "productProgramTitle", label: "Product Gate", width: 180 },
  { key: "categoryName", label: "Category", width: 140 },
  { key: "registrationCount", label: "Registrations", width: 120 },
  { key: "uniqueMembers", label: "Unique Members", width: 130 },
  { key: "attendedCount", label: "Attended", width: 100 },
  { key: "attendanceRate", label: "Attendance %", width: 110 },
  { key: "noShowCount", label: "No-Shows", width: 100 },
]

const formatPct = (value) => {
  const parsed = Number(value || 0)
  if (Number.isNaN(parsed)) {
    return "0.00%"
  }
  return `${parsed.toFixed(2)}%`
}

const EventsInsights = ({ dateRange, themeMode }) => {
  const [loading, setLoading] = useState(false)
  const [insights, setInsights] = useState(null)
  const [loadError, setLoadError] = useState(false)
  const [tierFilter, setTierFilter] = useState("all")
  const [syncingEventId, setSyncingEventId] = useState(null)
  const [syncingAll, setSyncingAll] = useState(false)
  const [syncMessage, setSyncMessage] = useState("")
  const [markNoShows, setMarkNoShows] = useState(false)

  const [openDataTableDlg, dataTableDlg] = DataTable()

  const loadInsights = useCallback(async () => {
    const idToken = localStorage.getItem(SESSION_VARIABLES.ACCESS_TOKEN)
    if (!idToken) {
      setTimeout(loadInsights, 500)
      return
    }
    try {
      setLoading(true)
      const response = await getEventInsights({
        startAt: getUnixEpochTime(dateRange[0]),
        endAt: getUnixEpochTime(dateRange[1]),
        tierAccess: tierFilter === "all" ? undefined : tierFilter,
        limitSize: 25,
      })
      verifyResponse(response)
      if (response.rtnCode === 200) {
        setInsights(response.data)
        setLoadError(false)
      } else {
        setLoadError(true)
      }
    } catch (e) {
      console.error("event insights loading error: ", e)
      setLoadError(true)
    } finally {
      setLoading(false)
    }
  }, [dateRange, tierFilter])

  useEffect(() => {
    loadInsights()
  }, [loadInsights])

  const handleSyncAttendance = async (event) => {
    try {
      setSyncingEventId(event.eventId)
      setSyncMessage("")
      const response = await syncZoomAttendance({
        eventId: event.eventId,
        markNoShows,
      })
      verifyResponse(response)
      if (response.rtnCode === 200) {
        const result = response.data || {}
        setSyncMessage(
          `"${event.title}": ${result.participantsFetched ?? 0} Zoom participants, ` +
            `${result.markedAttended ?? 0} newly marked attended, ` +
            `${result.alreadyAttended ?? 0} already attended, ` +
            `${result.markedNoShow ?? 0} marked no-show, ` +
            `${result.unmatchedParticipants ?? 0} participants without a matching member email.`
        )
        await loadInsights()
      } else {
        setSyncMessage(
          `Sync failed for "${event.title}": ${
            Array.isArray(response.message)
              ? response.message.join(", ")
              : response.message || "unknown error"
          }`
        )
      }
    } catch (e) {
      console.error("zoom attendance sync error: ", e)
      setSyncMessage(`Sync failed for "${event.title}". See console for details.`)
    } finally {
      setSyncingEventId(null)
    }
  }

  const handleSyncAll = async () => {
    try {
      setSyncingAll(true)
      setSyncMessage("")
      const response = await syncZoomAttendance({ markNoShows })
      verifyResponse(response)
      if (response.rtnCode === 200) {
        const result = response.data || {}
        const failures = (result.events || []).filter((e) => e.status === "failed")
        setSyncMessage(
          `Zoom sync complete: ${result.eventsSynced ?? 0} of ${result.eventsFound ?? 0} past events synced, ` +
            `${result.participantsFetched ?? 0} participants fetched, ` +
            `${result.markedAttended ?? 0} registrations newly marked attended` +
            (result.markedNoShow ? `, ${result.markedNoShow} marked no-show` : "") +
            (failures.length
              ? `. ${failures.length} events failed (e.g. "${failures[0].title}": ${failures[0].detail}).`
              : ".")
        )
        await loadInsights()
      } else {
        setSyncMessage(
          `Bulk Zoom sync failed: ${
            Array.isArray(response.message)
              ? response.message.join(", ")
              : response.message || "unknown error"
          }`
        )
      }
    } catch (e) {
      console.error("bulk zoom attendance sync error: ", e)
      setSyncMessage("Bulk Zoom sync failed. See console for details.")
    } finally {
      setSyncingAll(false)
    }
  }

  const openEventTable = (title, rows) => {
    openDataTableDlg({
      title,
      columns: EVENT_TABLE_FIELDS.map((field) => ({
        field: field.key,
        headerName: field.label,
        width: field.width,
        minWidth: 90,
      })),
      rows: (rows || []).map((row, idx) => ({
        ...row,
        id: row.eventId || `${title}-${idx}`,
        productProgramTitle: row.productProgramTitle || "-",
        categoryName: row.categoryName || "-",
      })),
    })
  }

  const summary = insights?.summary || {}
  const topByRegistrations = insights?.topByRegistrations || []
  const topByAttendance = insights?.topByAttendance || []
  const byTier = insights?.byTier || []
  const byProduct = insights?.byProduct || []
  const expertLeaderboard = insights?.expertLeaderboard || []
  const topicLeaderboard = insights?.topicLeaderboard || []
  const daily = insights?.daily || []

  const isDark = themeMode === "dark"
  const secondaryColor = isDark ? "#94a3b8" : "#64748b"

  const renderEventRow = (event) => (
    <li key={`event-${event.eventId}`}>
      <div>
        <div className="primary">{event.title}</div>
        <div className="secondary">
          {[
            event.eventType,
            event.tierLabel,
            event.tierKey === "product" && event.productProgramTitle
              ? event.productProgramTitle
              : null,
            event.startDate,
          ]
            .filter(Boolean)
            .join(" · ")}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <div style={{ textAlign: "right" }}>
          <div className="primary">{event.registrationCount || 0}</div>
          <div className="secondary">
            {`${event.attendedCount || 0} attended (${formatPct(event.attendanceRate)})`}
          </div>
        </div>
        {Number(event.hasZoomMeeting) === 1 && (
          <Tooltip title="Sync attendance from Zoom participant report">
            <span>
              <IconButton
                size="small"
                disabled={syncingEventId !== null || syncingAll}
                onClick={() => handleSyncAttendance(event)}
              >
                <SyncIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </div>
    </li>
  )

  return (
    <>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
        <ToggleButtonGroup
          value={tierFilter}
          exclusive
          size="small"
          onChange={(_, newValue) => newValue && setTierFilter(newValue)}
        >
          {TIER_FILTERS.map((tier) => (
            <ToggleButton value={tier.key} key={`tier-filter-${tier.key}`}>
              {tier.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={markNoShows}
              onChange={(e) => setMarkNoShows(e.target.checked)}
            />
          }
          label={
            <span style={{ fontSize: "12px", color: secondaryColor }}>
              Mark no-shows when syncing ended events
            </span>
          }
        />
        <Button
          variant="outlined"
          size="small"
          startIcon={<SyncIcon fontSize="small" />}
          disabled={syncingAll || syncingEventId !== null}
          onClick={handleSyncAll}
        >
          {syncingAll ? "Syncing from Zoom…" : "Sync all past events from Zoom"}
        </Button>
      </div>

      {loadError && (
        <div style={{ color: "#f87171", fontSize: "13px", marginBottom: "12px" }}>
          Event insights did not load. Confirm the latest `neurogym-app` build is deployed and your
          role has the `Stat-Get-Event-Insights` permission.
        </div>
      )}
      {syncMessage && (
        <div style={{ color: isDark ? "#a5b4fc" : "#4338ca", fontSize: "13px", marginBottom: "12px" }}>
          {syncMessage}
        </div>
      )}

      <StyledKpiGrid>
        <StyledKpiCard className={themeMode === "light" ? "light" : ""}>
          <div className="label">Event Registrations</div>
          <div className="value">{summary.registrationCount ?? 0}</div>
          <div className="meta">{`${summary.uniqueRegistrants ?? 0} unique members across ${
            summary.eventCount ?? 0
          } events`}</div>
        </StyledKpiCard>
        <StyledKpiCard className={themeMode === "light" ? "light" : ""}>
          <div className="label">Attended</div>
          <div className="value">{summary.attendedCount ?? 0}</div>
          <div className="meta">{`${summary.uniqueAttendees ?? 0} unique attendees`}</div>
        </StyledKpiCard>
        <StyledKpiCard className={themeMode === "light" ? "light" : ""}>
          <div className="label">Attendance Rate</div>
          <div className="value">{formatPct(summary.attendanceRate)}</div>
          <div className="meta">{`No-shows recorded: ${summary.noShowCount ?? 0}`}</div>
        </StyledKpiCard>
        <StyledKpiCard className={themeMode === "light" ? "light" : ""}>
          <div className="label">Events In Range</div>
          <div className="value">{summary.eventCount ?? 0}</div>
          <div className="meta">Events starting within the selected dates</div>
        </StyledKpiCard>
      </StyledKpiGrid>

      <div style={{ fontSize: "12px", color: secondaryColor, margin: "-8px 0 16px" }}>
        Attendance is populated from the Zoom participant report. Use "Sync all past events from
        Zoom" above (or the per-event sync button) — events that were never synced show zero
        attendance.
      </div>

      <StyledInsightGrid>
        <StyledInsightPanel className={isDark ? "dark" : ""}>
          <div className="panel-header">
            <h3 className="panel-title">Most Registered Events</h3>
            <div className="panel-actions">
              <Tooltip title="Open drill-down">
                <IconButton
                  size="small"
                  onClick={() => openEventTable("Most Registered Events", topByRegistrations)}
                >
                  <OpenInFullIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          </div>
          {topByRegistrations.length === 0 ? (
            <div className="empty">No event registrations in this period.</div>
          ) : (
            <StyledInsightList>{topByRegistrations.slice(0, 8).map(renderEventRow)}</StyledInsightList>
          )}
        </StyledInsightPanel>

        <StyledInsightPanel className={isDark ? "dark" : ""}>
          <div className="panel-header">
            <h3 className="panel-title">Most Attended Events</h3>
            <div className="panel-actions">
              <Tooltip title="Open drill-down">
                <IconButton
                  size="small"
                  onClick={() => openEventTable("Most Attended Events", topByAttendance)}
                >
                  <OpenInFullIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          </div>
          {topByAttendance.length === 0 ? (
            <div className="empty">
              No attendance recorded yet. Use the sync button on a Zoom event after it ends.
            </div>
          ) : (
            <StyledInsightList>{topByAttendance.slice(0, 8).map(renderEventRow)}</StyledInsightList>
          )}
        </StyledInsightPanel>

        <StyledInsightPanel className={isDark ? "dark" : ""}>
          <div className="panel-header">
            <div>
              <h3 className="panel-title">Experts Driving Attendance</h3>
              <div className="secondary" style={{ fontSize: "12px", marginTop: "4px", color: secondaryColor }}>
                Inferred by matching expert names inside event titles.
              </div>
            </div>
            <div className="panel-actions">
              <Tooltip title="Open drill-down">
                <IconButton
                  size="small"
                  onClick={() =>
                    openDataTableDlg({
                      title: "Experts (inferred from event titles)",
                      columns: [
                        { field: "expertName", headerName: "Expert", width: 220 },
                        { field: "eventCount", headerName: "Events", width: 100 },
                        { field: "registrationCount", headerName: "Registrations", width: 130 },
                        { field: "uniqueMembers", headerName: "Unique Members", width: 130 },
                        { field: "attendedCount", headerName: "Attended", width: 110 },
                        { field: "attendanceRate", headerName: "Attendance %", width: 120 },
                      ],
                      rows: expertLeaderboard.map((row, idx) => ({
                        ...row,
                        id: row.expertId || `expert-${idx}`,
                      })),
                    })
                  }
                >
                  <OpenInFullIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          </div>
          {expertLeaderboard.length === 0 ? (
            <div className="empty">
              No expert names matched event titles in this period.
            </div>
          ) : (
            <StyledInsightList>
              {expertLeaderboard.slice(0, 8).map((expert) => (
                <li key={`expert-${expert.expertId}`}>
                  <div>
                    <div className="primary">{expert.expertName}</div>
                    <div className="secondary">
                      {`${expert.eventCount || 0} events · ${expert.registrationCount || 0} registrations`}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div className="primary">{expert.attendedCount || 0}</div>
                    <div className="secondary">{formatPct(expert.attendanceRate)}</div>
                  </div>
                </li>
              ))}
            </StyledInsightList>
          )}
        </StyledInsightPanel>

        <StyledInsightPanel className={isDark ? "dark" : ""}>
          <div className="panel-header">
            <div>
              <h3 className="panel-title">Topics Driving Attendance</h3>
              <div className="secondary" style={{ fontSize: "12px", marginTop: "4px", color: secondaryColor }}>
                From event categories and tags.
              </div>
            </div>
            <div className="panel-actions">
              <Tooltip title="Open drill-down">
                <IconButton
                  size="small"
                  onClick={() =>
                    openDataTableDlg({
                      title: "Topics (categories and tags)",
                      columns: [
                        { field: "topic", headerName: "Topic", width: 220 },
                        { field: "source", headerName: "Source", width: 110 },
                        { field: "eventCount", headerName: "Events", width: 100 },
                        { field: "registrationCount", headerName: "Registrations", width: 130 },
                        { field: "attendedCount", headerName: "Attended", width: 110 },
                        { field: "attendanceRate", headerName: "Attendance %", width: 120 },
                      ],
                      rows: topicLeaderboard.map((row, idx) => ({
                        ...row,
                        id: `topic-${row.source}-${row.topic}-${idx}`,
                      })),
                    })
                  }
                >
                  <OpenInFullIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          </div>
          {topicLeaderboard.length === 0 ? (
            <div className="empty">No topics with registrations in this period.</div>
          ) : (
            <StyledInsightList>
              {topicLeaderboard.slice(0, 8).map((topic, idx) => (
                <li key={`topic-${topic.source}-${topic.topic}-${idx}`}>
                  <div>
                    <div className="primary">{topic.topic}</div>
                    <div className="secondary">
                      {`${topic.source} · ${topic.eventCount || 0} events · ${
                        topic.registrationCount || 0
                      } registrations`}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div className="primary">{topic.attendedCount || 0}</div>
                    <div className="secondary">{formatPct(topic.attendanceRate)}</div>
                  </div>
                </li>
              ))}
            </StyledInsightList>
          )}
        </StyledInsightPanel>

        <StyledInsightPanel className={isDark ? "dark" : ""}>
          <div className="panel-header">
            <h3 className="panel-title">Breakdown by Tier</h3>
          </div>
          {byTier.length === 0 ? (
            <div className="empty">No tier data in this period.</div>
          ) : (
            <StyledInsightList>
              {byTier.map((tier) => (
                <li key={`tier-${tier.tierKey}`}>
                  <div>
                    <div className="primary">{tier.tierLabel}</div>
                    <div className="secondary">
                      {`${tier.eventCount || 0} events · ${tier.uniqueMembers || 0} unique members`}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div className="primary">{tier.registrationCount || 0}</div>
                    <div className="secondary">
                      {`${tier.attendedCount || 0} attended (${formatPct(tier.attendanceRate)})`}
                    </div>
                  </div>
                </li>
              ))}
            </StyledInsightList>
          )}
        </StyledInsightPanel>

        <StyledInsightPanel className={isDark ? "dark" : ""}>
          <div className="panel-header">
            <div>
              <h3 className="panel-title">Product-Gated Events</h3>
              <div className="secondary" style={{ fontSize: "12px", marginTop: "4px", color: secondaryColor }}>
                Events gated by a product entitlement, grouped by product.
              </div>
            </div>
          </div>
          {byProduct.length === 0 ? (
            <div className="empty">No product-gated event activity in this period.</div>
          ) : (
            <StyledInsightList>
              {byProduct.map((product) => (
                <li key={`product-${product.productProgramId}`}>
                  <div>
                    <div className="primary">{product.productTitle}</div>
                    <div className="secondary">
                      {`${product.eventCount || 0} events · ${product.uniqueMembers || 0} unique members`}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div className="primary">{product.registrationCount || 0}</div>
                    <div className="secondary">
                      {`${product.attendedCount || 0} attended (${formatPct(product.attendanceRate)})`}
                    </div>
                  </div>
                </li>
              ))}
            </StyledInsightList>
          )}
        </StyledInsightPanel>

        <StyledInsightPanel className={isDark ? "dark" : ""}>
          <div className="panel-header">
            <h3 className="panel-title">Registrations Over Time</h3>
          </div>
          {daily.length === 0 ? (
            <div className="empty">No daily registration data in this period.</div>
          ) : (
            <StyledAgentTrendChart>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={daily}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" hide />
                  <YAxis allowDecimals={false} />
                  <RechartsTooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="registrationCount"
                    name="Registrations"
                    stroke="#38bdf8"
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="attendedCount"
                    name="Attended"
                    stroke="#34d399"
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </StyledAgentTrendChart>
          )}
        </StyledInsightPanel>
      </StyledInsightGrid>

      <LoadingScreen loading={loading} />
      {dataTableDlg}
    </>
  )
}

export default EventsInsights
